import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export interface Shop {
  id: string;
  ownerId: string;
  name: string;
  phone: string;
  address: string;
  logoUrl: string | null;
  totalCapital: number;
  // Thông tin in trên hợp đồng (trống → fallback name/address/phone)
  printName: string;
  printAddress1: string;
  printAddress2: string;
  // Cấu hình khoản vay
  defaultInterestRate: number;
  defaultLoanDays: number;
  remindBeforeDays: number;
  maxLoanRatio: number;
  // Thông báo & tự động hoá
  emailNotif: boolean;
  autoRemind: boolean;
  autoBackup: boolean;
  // Danh mục tài sản tuỳ chỉnh
  loanCategories: string[];
  warehouses: string[];
}

// Các trường cho phép cập nhật từ client (camelCase)
export type ShopFields = Omit<Shop, 'id' | 'ownerId' | 'totalCapital'>;

interface ShopRow {
  id: string;
  owner_id: string;
  name: string;
  phone: string;
  address: string;
  logo_url: string | null;
  total_capital: number;
  print_name: string;
  print_address1: string;
  print_address2: string;
  default_interest_rate: number;
  default_loan_days: number;
  remind_before_days: number;
  max_loan_ratio: number;
  email_notif: boolean;
  auto_remind: boolean;
  auto_backup: boolean;
  loan_categories: unknown;
  warehouses: unknown;
}

function mapShop(row: ShopRow): Shop {
  return {
    id: row.id,
    ownerId: row.owner_id,
    name: row.name || '',
    phone: row.phone || '',
    address: row.address || '',
    logoUrl: row.logo_url,
    totalCapital: Number(row.total_capital) || 0,
    printName: row.print_name || '',
    printAddress1: row.print_address1 || '',
    printAddress2: row.print_address2 || '',
    defaultInterestRate: Number(row.default_interest_rate ?? 3),
    defaultLoanDays: Number(row.default_loan_days ?? 30),
    remindBeforeDays: Number(row.remind_before_days ?? 3),
    maxLoanRatio: Number(row.max_loan_ratio ?? 75),
    emailNotif: row.email_notif !== false,
    autoRemind: row.auto_remind !== false,
    autoBackup: row.auto_backup === true,
    loanCategories: Array.isArray(row.loan_categories) ? (row.loan_categories as string[]) : [],
    warehouses: Array.isArray(row.warehouses) ? (row.warehouses as string[]) : [],
  };
}

// camelCase → snake_case cho các trường update
const FIELD_TO_COLUMN: Record<keyof ShopFields, string> = {
  name: 'name',
  phone: 'phone',
  address: 'address',
  logoUrl: 'logo_url',
  printName: 'print_name',
  printAddress1: 'print_address1',
  printAddress2: 'print_address2',
  defaultInterestRate: 'default_interest_rate',
  defaultLoanDays: 'default_loan_days',
  remindBeforeDays: 'remind_before_days',
  maxLoanRatio: 'max_loan_ratio',
  emailNotif: 'email_notif',
  autoRemind: 'auto_remind',
  autoBackup: 'auto_backup',
  loanCategories: 'loan_categories',
  warehouses: 'warehouses',
};

export interface SubscriptionState {
  expiresAt: string | null;
  daysLeft: number | null;
  isActive: boolean;
  isExpiringSoon: boolean;
}

export function useShop() {
  const { user } = useAuth();
  const [shop, setShop] = useState<Shop | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionState>({
    expiresAt: null, daysLeft: null, isActive: false, isExpiringSoon: false,
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setShop(null);
      setLoading(false);
      return;
    }
    try {
      // Tìm shop qua profile.shop_id trước (nhân viên + chủ tiệm đều có)
      const { data: profile } = await supabase
        .from('profiles')
        .select('shop_id')
        .eq('id', user.id)
        .maybeSingle();

      let row: ShopRow | null = null;
      if (profile?.shop_id) {
        const { data } = await supabase.from('shops').select('*').eq('id', profile.shop_id).maybeSingle();
        row = data as ShopRow | null;
      }

      // Fallback: tìm theo owner (chủ tiệm lần đầu vào)
      if (!row) {
        const { data } = await supabase.from('shops').select('*').eq('owner_id', user.id).maybeSingle();
        row = data as ShopRow | null;
        // Gắn shop vào profile nếu chưa có
        if (row && !profile?.shop_id) {
          await supabase.from('profiles').update({ shop_id: row.id }).eq('id', user.id);
        }
      }

      setShop(row ? mapShop(row) : null);

      // Trạng thái thuê bao của tiệm
      if (row) {
        const { data: sub } = await supabase
          .from('subscriptions')
          .select('expires_at')
          .eq('shop_id', row.id)
          .eq('status', 'active')
          .order('expires_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        const expiresAt = (sub?.expires_at as string) || null;
        const daysLeft = expiresAt
          ? Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000)
          : null;
        setSubscription({
          expiresAt,
          daysLeft,
          isActive: daysLeft !== null && daysLeft > 0,
          isExpiringSoon: daysLeft !== null && daysLeft > 0 && daysLeft <= 7,
        });
      } else {
        setSubscription({ expiresAt: null, daysLeft: null, isActive: false, isExpiringSoon: false });
      }
    } catch {
      setShop(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
    const onUpdated = () => { load(); };
    window.addEventListener('shop-updated', onUpdated);
    return () => window.removeEventListener('shop-updated', onUpdated);
  }, [load]);

  // Lưu thông tin tiệm: có thì update, chưa có thì tạo mới rồi gắn vào profile
  // Chấp nhận Partial<ShopFields> — chỉ update các trường được truyền
  const saveShop = useCallback(async (fields: Partial<ShopFields>) => {
    if (!user) return { error: new Error('Chưa đăng nhập') };

    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    (Object.keys(FIELD_TO_COLUMN) as (keyof ShopFields)[]).forEach((key) => {
      const val = fields[key];
      if (val === undefined) return;
      payload[FIELD_TO_COLUMN[key]] =
        typeof val === 'string' ? val.trim() : val;
    });

    if (shop) {
      const { error } = await supabase.from('shops').update(payload).eq('id', shop.id);
      if (error) return { error };
      const merged = { ...shop };
      (Object.keys(fields) as (keyof ShopFields)[]).forEach((key) => {
        const val = fields[key];
        if (val !== undefined) (merged as Record<string, unknown>)[key] = typeof val === 'string' ? val.trim() : val;
      });
      setShop(merged);
      window.dispatchEvent(new Event('shop-updated'));
      return { error: null };
    }

    const { data, error } = await supabase
      .from('shops')
      .insert({ owner_id: user.id, ...payload })
      .select()
      .single();
    if (error) return { error };

    const newShop = mapShop(data as ShopRow);
    await supabase.from('profiles').update({ shop_id: newShop.id }).eq('id', user.id);
    setShop(newShop);
    window.dispatchEvent(new Event('shop-updated'));
    return { error: null };
  }, [user, shop]);

  // Upload logo lên Supabase Storage, trả về public URL
  const uploadLogo = useCallback(async (file: File) => {
    if (!shop) return { url: null, error: new Error('Chưa có thông tin tiệm') };
    const ext = file.name.split('.').pop() || 'png';
    const path = `${shop.id}/logo.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('shop-assets')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) return { url: null, error: upErr };

    const { data } = supabase.storage.from('shop-assets').getPublicUrl(path);
    // Thêm timestamp để tránh cache ảnh cũ
    const url = `${data.publicUrl}?v=${Date.now()}`;
    const { error: updErr } = await supabase.from('shops').update({ logo_url: url }).eq('id', shop.id);
    if (updErr) return { url: null, error: updErr };
    setShop({ ...shop, logoUrl: url });
    window.dispatchEvent(new Event('shop-updated'));
    return { url, error: null };
  }, [shop]);

  return { shop, subscription, loading, saveShop, uploadLogo, reload: load };
}
